import classnames from 'classnames';
import {
	BadgeIcon, Button, Confirmation, connectedComponent, Icon, List, LoadingIndicator, SortableTable
} from 'gwx-voyager-components';
import moment, { tz } from 'moment-timezone';
import PropTypes from 'prop-types';
import { stringify } from 'query-string';
import React, { Component } from 'react';
import {
	loadClientInvitations, revokeClientInvitation, sendClientInvitation, showActivationKey
} from '../clientInvitations.actions';
import './client-invitations-list.scss';

/**
 * Component representing a list of Client Invitations.
 *
 * @class ClientInvitationsList
 * @extends {Component}
 */
class ClientInvitationsList extends Component {
	constructor(props) {
		super(props);
		this.onClickResend = this.onClickResend.bind(this);
		this.onClickRevoke = this.onClickRevoke.bind(this);
		this.discardConfirmation = this.discardConfirmation.bind(this);
		this.filterResults = this.filterResults.bind(this);
		this.updateFilterSelection = this.updateFilterSelection.bind(this);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleSort = this.handleSort.bind(this);
		this.exportData = this.exportData.bind(this);
		this.onClickRefresh = this.onClickRefresh.bind(this);
		this.showInviteOtherAdminPrivilege=this.showInviteOtherAdminPrivilege.bind(this);
		this.state = {
			buildDownloadUrl: null,
			confirmation: null,
			filter: {
				display: 'Name',
				componentClass: 'input',
				id: 'name',
				value: ''
			},
			filterCriteria: {
				name: null,
				communityId: null,
				email: null
			}
		};
	}

	componentDidMount() {
		const query = { ...this.props.query, communityId: this.props.communityId };
		this.props.fetchClientInvitations(query);
	}

	shouldComponentUpdate(nextProps) {
		if (JSON.stringify(nextProps.query) !== JSON.stringify(this.props.query)) {
			const query = { ...nextProps.query, communityId: this.props.communityId };
			this.props.fetchClientInvitations(query);
			return false;
		}

		return true;
	}

	exportData() {
		this.setState({
			buildDownloadUrl:
				`/api/clientInvitations/export?${stringify({
					...this.props.query,
					communityId: this.props.communityId,
				})}`
		});
	}

	onClickRefresh() {
		const query = { ...this.props.query, communityId: this.props.communityId };
		this.props.fetchClientInvitations(query);
	}

	handleInputChange(e) {
		let value = e;
		value = value ? value.target && value.target.value : '';
		this.setState((prevState) => ({
			filter: { ...prevState.filter, value }
		}));
	}

	handleSort(sortBy) {
		if (sortBy && sortBy.length > 0 && this.props.sort && this.props.sort.length > 0) {
			const { sort } = this.props;
			const desc = sort[0].direction === 'DESC';
			const sortProperty = sort[0].property === 'lastName' ? 'name' : sort[0].property;

			if (sortBy[0].id !== sortProperty || sortBy[0].desc !== desc) {
				const newSort = {
					direction: sortBy[0].desc ? 'DESC' : 'ASC',
					property: sortBy[0].id,
					page: 0
				};

				const query = {
					...this.props.query,
					...newSort
				};
				this.props.onSort(query);
			}
		}
	}

	onClickResend(clientInvitation, e) {
		const confirmationProps = {
			body: 'Are you sure you wish to send this Invitation again?',
			id: `resend-${clientInvitation.id}-confirmation`,
			onClickCancel: this.discardConfirmation,
			onClickConfirm: () => this.props.resendClientInvitation(clientInvitation.id, this.props.query,
				{ ...clientInvitation, status: 1 })
				.then(this.discardConfirmation),
			placement: 'left',
			target: e.currentTarget,
			title: 'Confirm Resend'
		};
		this.setState({ confirmation: <Confirmation {...confirmationProps} /> });
	}

	onClickRevoke(clientInvitation, e) {
		const confirmationProps = {
			body: 'Are you sure you wish to revoke this Invitation?',
			id: `revoke-${clientInvitation.id}-confirmation`,
			onClickCancel: this.discardConfirmation,
			onClickConfirm: () => this.props.revokeClientInvitation(clientInvitation.id, this.props.query,
				{ ...clientInvitation, status: 8 })
				.then(this.discardConfirmation),
			placement: 'left',
			target: e.currentTarget,
			title: 'Confirm Revocation'
		};
		this.setState({ confirmation: <Confirmation {...confirmationProps} /> });
	}

	filterResults(value, key) {
		let query = { ...this.props.query, ...this.state.filterCriteria };
		query = Object.keys(query).reduce((acc, id) => {
			if (id === key) {
				acc[id] = value;
			} else if (!query[id]) {
				delete acc[id];
			}
			return acc;
		}, {});
		this.props.onFilter(query);
	}

	updateFilterSelection(key, data) {
		// eslint-disable-next-line react/no-access-state-in-setstate
		const filterCriteria = Object.keys(this.state.filterCriteria).reduce((acc, id) => {
			acc[id] = null;
			return acc;
		}, {});
		const filter = {
			display: data,
			title: data,
			componentClass: 'input',
			id: key,
			value: ''
		};
		this.setState({ filter, filterCriteria });
	}

	discardConfirmation() {
		this.setState({ confirmation: null });
	}

	showInviteOtherAdminPrivilege(isAdminInvitationAllowed){
		return isAdminInvitationAllowed? <Icon iconName='check solid' iconStyle='fa lg'/>:''
	}

	render() {
		const mask = Array(8).fill('*').join('');

		const rows = this.props.clientInvitations.map((inv) => {
			const activationKeyDisabled = !!inv.activationKey || inv.status !== 1;
			const badgeProps = {
				1: {
					iconName: 'paper-plane',
					iconStyle: 'text-primary',
					tooltip: 'Sent'
				},
				2: {
					iconName: 'check',
					iconStyle: 'text-success',
					tooltip: 'Accepted'
				},
				4: {
					iconName: 'times',
					iconStyle: 'text-muted',
					tooltip: 'Declined'
				},
				8: {
					iconName: 'ban',
					iconStyle: 'text-danger',
					tooltip: 'Revoked'
				}
			};
			return {
				id: inv.id,
				status: <BadgeIcon key={`${inv.id}-badge`} {...badgeProps[inv.status]} />,
				name: `${inv.lastName}, ${inv.firstName}`,
				email: inv.email,
				isAdminInvitationAllowed: this.showInviteOtherAdminPrivilege(inv.isAdminInvitationAllowed),
				lastModifiedDateTime: moment(inv.lastModifiedDateTime).tz(tz.guess()).format('MM/DD/YYYY hh:mm A z'),
				activationKey: (
					<span
						id={`${inv.id}-activation-key`}
						key={`${inv.id}-activation-key`}
					>
						<span className='activation-key-mask'>{inv.activationKey || mask}</span>
						<Button
							className={classnames('btn-link', 'activation-key-btn')}
							disabled={activationKeyDisabled}
							id={`${inv.id}-activation-key-btn`}
							onClick={() => this.props.getActivationKey(inv.id)}
							title={activationKeyDisabled ? '' : 'Show Activation Key'}
						>
							<Icon iconName={activationKeyDisabled ? 'eye-slash' : 'eye'} />
						</Button>
					</span>),
				icons: (
					<span key={`${inv.id}-action-buttons`}>
						<Button
							className='btn-link'
							disabled={inv.status === 2}
							id={`${inv.id}-resend-btn`}
							onClick={(e) => this.onClickResend(inv, e)}
							title='Resend Invitation'
						>
							<Icon iconName='paper-plane-o' iconStyle='fa-lg' />
						</Button>
						<Button
							className='btn-link'
							disabled={inv.status === 8}
							id={`${inv.id}-revoke-btn`}
							onClick={(e) => this.onClickRevoke(inv, e)}
							title='Revoke Invitation'
						>
							<Icon iconName='ban' iconStyle='fa-lg' />
						</Button>
					</span>)
			};
		});

		const listProps = {
			action: {
				id: 'export-data-button',
				title: 'Export Data',
				onClick: this.exportData,
				className: 'btn btn-outline-primary my-2 my-sm-0',
				iconName: 'file-excel-o',
				disabled: this.props.clientInvitations.length===0,
			},
			create: {
				id: 'create-invitation-button',
				title: 'Create Invitation',
				onClick: this.props.onCreate
			},
			refresh: {
				id: 'refresh-invitation-button',
				title: 'Refresh Invitations',
				onClick: this.onClickRefresh
			},
			gridClass: 'd-none',
			isFetching: false,
			pagination: {
				nextPage: this.props.nextPage,
				previousPage: this.props.previousPage,
				startCount: this.props.startCount,
				endCount: this.props.endCount,
				total: this.props.totalCount,
				sort: this.props.sort
			},
			filter: {
				id: this.state.filter.id,
				onFilter: this.filterResults,
				componentClass: this.state.filter.componentClass,
				value: this.state.filter.value,
				onChange: this.handleInputChange,
				dropdown: {
					id: this.state.filter.id,
					title: this.state.filter.title,
					display: this.state.filter.display,
					options: [{
						id: 'name',
						value: 'Name'
					}, {
						id: 'email',
						value: 'Email'
					}]
				},
				dropDownPlacement: 'left',
				filterButtonIcon: 'search'
			},
			updateQueryParam: this.updateFilterSelection,
			rows: []
		};

		const headers = [
			{
				Header: 'Status',
				accessor: 'status'
			},
			{
				Header: 'Name',
				accessor: 'name'
			},
			{
				Header: 'Email',
				accessor: 'email'
			},
			{
				Header: 'Invite Others',
				accessor: 'isAdminInvitationAllowed',
				disableSortBy: true
			},
			{
				Header: 'Last Modified',
				accessor: 'lastModifiedDateTime'
			},
			{
				Header: 'Activation Key',
				accessor: 'activationKey',
				disableSortBy: true
			},
			{
				Header: '',
				accessor: 'icons',
				disableSortBy: true
			}
		];

		const sortProperty = (this.props.sort && this.props.sort[0] && this.props.sort[0].property) || 'lastModifiedDateTime';
		const initialState = {
			sortBy: [
				{
					id: sortProperty === 'lastName' ? 'name' : sortProperty,
					desc: (this.props.sort && this.props.sort[0] && this.props.sort[0].direction) ? this.props.sort[0].direction === 'DESC' : true
				}
			]
		};

		const gridProps = {
			columns: headers,
			className: 'client-invitations-grid',
			data: rows,
			manualSortBy: true,
			disableMultiSort: true,
			disableSortRemove: true,
			disableMultiRemove: true,
			onSort: (sortBy) => this.handleSort(sortBy),
			initialState
		};

		return (
			<div>
				<List {...listProps} />
				{this.props.isFetching ? <LoadingIndicator /> : <SortableTable {...gridProps} />}
				{this.state.confirmation}
				<iframe
					title='Build Download iFrame'
					name='build-download-iframe'
					id='build-download-iframe'
					style={{ display: 'none' }}
					src={this.state.buildDownloadUrl}
				/>
			</div>
		);
	}
}
ClientInvitationsList.propTypes = {
	fetchClientInvitations: PropTypes.func.isRequired,
	getActivationKey: PropTypes.func.isRequired,
	clientInvitations: PropTypes.arrayOf(PropTypes.shape({
		activationKey: PropTypes.string,
		communityName: PropTypes.string.isRequired,
		communityId: PropTypes.string.isRequired,
		email: PropTypes.string.isRequired,
		firstName: PropTypes.string.isRequired,
		id: PropTypes.string.isRequired,
		lastName: PropTypes.string.isRequired,
		status: PropTypes.number.isRequired
	})).isRequired,
	isFetching: PropTypes.bool.isRequired,
	onCreate: PropTypes.func.isRequired,
	onFilter: PropTypes.func.isRequired,
	resendClientInvitation: PropTypes.func.isRequired,
	revokeClientInvitation: PropTypes.func.isRequired,
	onSort: PropTypes.func.isRequired,
	sort: PropTypes.arrayOf(PropTypes.shape({
		direction: PropTypes.string,
		property: PropTypes.string
	}))
};

ClientInvitationsList.defaultProps = {
	sort: []
};

/**
 * Maps the redux store's state to a set of props
 * to be passed to the ClientInvitationsList component.
 *
 * @param {object} state
 * 		The current state of the redux store.
 */
const mapStateToProps = (state, props) => {
	const {
		entities: { clientInvitations }
	} = state;

	const {
		match: { params: { id: communityId } },
	} = props;

	return {
		communityId,
		isFetching: clientInvitations.isFetching,
		context: `${communityId}/clientInvitations`,
		clientInvitations: clientInvitations.ids.map((id) => clientInvitations.results[id]),
		component: 'clientInvitations'
	};
};

/**
 * Creates a set of functions that utilize the dispatcher to
 * dispatch actions. These are the Action Creators for the
 * ClientInvitationsList component.
 *
 * @param {function} dispatch
 *		The redux dispatcher.
 */
const mapDispatchToProps = (dispatch, props) => ({
	/**
	 * Makes the fetch request for the list of Invitations.
	 */
	fetchClientInvitations: (query) => dispatch(loadClientInvitations(query)),
	/**
	 * Makes the fetch request for an Invitation's activation key.
	 *
	 * @param {string} id
	 * 		The ID of the invitation to request the activation key for.
	 */
	getActivationKey: (id) => dispatch(showActivationKey(id)),
	/**
	 * Handler for clicking the create button.
	 */
	onCreate: () => props.history.push(`/${props.match.params.id}/clientInvitations/add${props.location.search}`),
	/**
	 * Makes the query changes that will request filtered results
	 */
	onFilter: (query) => props.history.push(`${props.location.pathname}?${stringify(query)}`),
	/**
	 * Makes the query changes that will request sorted results
	 */
	onSort: (query) => props.history.push(`${props.location.pathname}?${stringify(query)}`),
	/**
	 * Makes a request for resending the specified Invitation.
	 *
	 * @param {string} id
	 * 		The ID of the Invitation to resend.
	 * @param {Object} query
	 * 		The current set of query parameters.
	 * @param {Object} invitation
	 * 		The invitation to be sent as the request body.
	 */
	resendClientInvitation: (id, query, invitation) => dispatch(sendClientInvitation(id, invitation, query)),
	/**
	 * Makes a request for revoking the specified Invitation.
	 *
	 * @param {string} id
	 * 		The ID of the Invitation to revoke.
	 * @param {Object} query
	 * 		The current set of query parameters.
	 * @param {Object} invitation
	 * 		The invitation to be sent as the request body.
	 */
	revokeClientInvitation: (id, query, invitation) => dispatch(revokeClientInvitation(id, invitation, query))
});

const ClientInvitationsListContainer = connectedComponent(ClientInvitationsList, mapStateToProps, mapDispatchToProps, true, true);

export { ClientInvitationsListContainer as default, ClientInvitationsList };
